import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing search functionality with debouncing
 *
 * @param {string} initialValue - Initial search value (e.g., from URL params)
 * @param {number} delay - Debounce delay in milliseconds (default: 500ms)
 * @param {function} onSearchChange - Optional callback when search term changes
 * @returns {object} Search state and handlers
 */
export const useSearchWithDebounce = (initialValue = '', delay = 500, onSearchChange = null) => {
    // Actual search term used for API calls (debounced)
    const [searchTerm, setSearchTerm] = useState(initialValue);

    // Input value for immediate UI feedback
    const [searchInputValue, setSearchInputValue] = useState(initialValue);

    // Ref to store timeout ID for cleanup
    const debounceTimeoutRef = useRef(null);

    // Call callback when searchTerm changes (after debounce)
    useEffect(() => {
        if (onSearchChange) {
            onSearchChange(searchTerm);
        }
    }, [searchTerm, onSearchChange]);

    /**
     * Handle search input changes with debouncing
     * Updates UI immediately but delays API call
     */
    const handleSearchInputChange = useCallback((e) => {
        const value = e.target.value;

        // Update input value immediately for responsive UI
        setSearchInputValue(value);

        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout for debounced search
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
        }, delay);
    }, [delay]);

    /**
     * Allow external updates to search value
     * Useful for URL parameter initialization or programmatic updates
     */
    const updateSearchValue = useCallback((value) => {
        setSearchTerm(value);
        setSearchInputValue(value);
    }, []);

    /**
     * Clear the search
     */
    const clearSearch = useCallback(() => {
        setSearchTerm('');
        setSearchInputValue('');
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return {
        searchTerm,           // Debounced value for API calls
        searchInputValue,     // Current input value for UI
        handleSearchInputChange,  // Input onChange handler
        updateSearchValue,    // Function to update search externally
        clearSearch          // Function to clear search
    };
};
